import { useEffect, useState, useMemo } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Heart, ChatCircle, Flag, Package, Truck, Certificate, ArrowsClockwise, HandCoins, Trophy, CaretLeft, CaretRight, Warning, ShoppingCartSimple } from "@phosphor-icons/react";
import { api, imgUrl, formatErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import { neighborsOf } from "@/lib/browseContext";
import { readBrowseReturn } from "@/lib/browsePosition";
import LikeButton from "@/components/LikeButton";
import PaymentDisclaimer from "@/components/PaymentDisclaimer";
import { FoundingHat } from "@/components/FoundingHat";
import LegalNotice from "@/components/LegalNotice";
import HiddenNeedle from "@/components/HiddenNeedle";

export default function ListingDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const cart = useCart();
  const navigate = useNavigate();
  const [listing, setListing] = useState(null);
  const [seller, setSeller] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saved, setSaved] = useState(false);
  const [msgOpen, setMsgOpen] = useState(false);
  const [msgText, setMsgText] = useState("");
  const [paymentModal, setPaymentModal] = useState(null);
  const [reportOpen, setReportOpen] = useState(false);
  const [reportReason, setReportReason] = useState("Suspected scam");
  const [transactions, setTransactions] = useState([]);
  const [txBusy, setTxBusy] = useState(false);
  const [likedByMe, setLikedByMe] = useState(false);
  const [purchaseOpen, setPurchaseOpen] = useState(false);
  const [acceptPurchaseTerms, setAcceptPurchaseTerms] = useState(false);
  const [chosenMethod, setChosenMethod] = useState(null);
  const [contest, setContest] = useState(null);

  // Fetch active Hunt-the-Needle contest so HiddenNeedle knows whether to
  // render on THIS listing (based on target_listing_id + is_closed).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/contests/needle/current");
        if (!cancelled) setContest(data?.contest || null);
      } catch { /* no contest, no needle */ }
    })();
    return () => { cancelled = true; };
  }, []);

  const loadTx = async () => {
    try {
      const { data } = await api.get(`/transactions/for-listing/${id}`);
      setTransactions(data.transactions || []);
    } catch { setTransactions([]); }
  };

  // Prev/Next context — remembered from the browse/watchlist/profile/mine
  // grid the user came from. Recomputed on every id change.
  const nav = useMemo(() => neighborsOf(id), [id]);

  // Keyboard shortcuts: ← previous, → next. Ignored while typing in an input.
  useEffect(() => {
    const onKey = (e) => {
      const tag = (e.target?.tagName || "").toLowerCase();
      if (tag === "input" || tag === "textarea" || e.target?.isContentEditable) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (e.key === "ArrowLeft" && nav.prev) navigate(`/listing/${nav.prev}`);
      if (e.key === "ArrowRight" && nav.next) navigate(`/listing/${nav.next}`);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [nav.prev, nav.next, navigate]);

  useEffect(() => {
    setLoading(true);
    window.scrollTo({ top: 0, behavior: "auto" });
    (async () => {
      try {
        const { data } = await api.get(`/listings/${id}`);
        setListing(data.listing);
        setSeller(data.seller);
        // Check watchlist status
        const w = await api.get("/watchlist");
        setSaved(w.data.listings.some((l) => l.id === id));
        // Hydrate like-by-me state (silent 401 for guests).
        try {
          const likes = await api.get("/listings/likes/mine");
          setLikedByMe((likes.data.listing_ids || []).includes(id));
        } catch { setLikedByMe(false); }
        await loadTx();
      } catch (err) {
        toast.error(formatErr(err));
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  const toggleWatch = async () => {
    try {
      if (saved) {
        await api.delete(`/watchlist/${id}`);
        setSaved(false);
        toast.success("Removed from watchlist");
      } else {
        await api.post(`/watchlist/${id}`);
        setSaved(true);
        toast.success("Added to watchlist");
      }
    } catch (err) { toast.error(formatErr(err)); }
  };

  const sendMessage = async () => {
    if (!msgText.trim()) return;
    try {
      const { data } = await api.post("/messages/start", {
        recipient_id: listing.seller_id,
        listing_id: listing.id,
        text: msgText.trim(),
      });
      toast.success("Message sent");
      setMsgOpen(false);
      setMsgText("");
      navigate(`/messages/${data.conversation.id}`);
    } catch (err) { toast.error(formatErr(err)); }
  };

  const openPayment = (method) => {
    let link = null;
    if (method === "paypal") link = seller?.paypal_link;
    if (method === "cashapp") link = seller?.cashapp_handle;
    if (method === "venmo") link = seller?.venmo_link;
    setPaymentModal({ method, link });
  };

  const submitReport = async () => {
    try {
      await api.post("/reports", { target_type: "listing", target_id: id, reason: reportReason });
      toast.success("Report submitted");
      setReportOpen(false);
    } catch (err) { toast.error(formatErr(err)); }
  };

  const openPurchase = () => {
    if (!user) { navigate("/login"); return; }
    setAcceptPurchaseTerms(false);
    setChosenMethod(null);
    setPurchaseOpen(true);
  };

  const submitPurchase = async () => {
    if (!chosenMethod) {
      toast.error("Choose how you'll pay the seller first.");
      return;
    }
    if (!acceptPurchaseTerms) {
      toast.error("Please read and accept the Purchase Terms first.");
      return;
    }
    setTxBusy(true);
    try {
      await api.post("/transactions", {
        listing_id: id,
        payment_method: chosenMethod,
        accept_transaction_terms: true,
      });
      toast.success("Purchase Order sent — seller notified.");
      setPurchaseOpen(false);
      navigate("/transactions");
    } catch (err) { toast.error(formatErr(err)); }
    finally { setTxBusy(false); }
  };

  if (loading) return <div className="py-24 text-center font-mono text-xs uppercase tracking-widest text-zinc-500">Loading…</div>;
  if (!listing) return <div className="py-24 text-center text-zinc-500">Listing not found.</div>;

  const isOwn = user?.id === listing.seller_id;
  // Detail page shows the full-size original — no resize needed.
  const front = imgUrl(listing.front_image, { w: 1200 });
  const back = imgUrl(listing.back_image, { w: 1200 });

  const chips = [];
  if (listing.base) chips.push({ label: "Base", cls: "" });
  if (listing.insert) chips.push({ label: "Insert", cls: "h57-chip-blue" });
  if (listing.variation) chips.push({ label: "Variation", cls: "h57-chip-blue" });
  if (listing.rookie) chips.push({ label: "Rookie", cls: "h57-chip-orange" });
  if (listing.autograph) chips.push({ label: "Auto", cls: "h57-chip-gold" });
  if (listing.relic) {
    const label = listing.relic_type === "player_worn" ? "Player Worn Relic" : listing.relic_type === "game_used" ? "Game Used Relic" : "Relic";
    chips.push({ label, cls: "" });
  }
  if (listing.numbered) chips.push({ label: listing.serial_number ? `/${listing.serial_number}` : "Numbered", cls: "" });
  if (listing.ssp) chips.push({ label: "SSP", cls: "h57-chip-gold" });
  if (listing.sp) chips.push({ label: "SP", cls: "h57-chip-gold" });
  if (listing.graded) chips.push({ label: `${listing.grading_company || ""} ${listing.grade || ""}`.trim(), cls: "h57-chip-blue" });

  return (
    <div className="max-w-6xl mx-auto px-4 md:px-8 py-4 md:py-8 relative" data-needle-anchor="page">
      <HiddenNeedle listingId={listing.id} contest={contest} />
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <button
          type="button"
          onClick={() => {
            // Prefer the exact Browse URL the user left off on (preserves
            // sort, filters, search text). Falls back to plain /browse when
            // arriving cold (e.g. from a shared link).
            const ret = readBrowseReturn();
            navigate(ret?.url || "/browse");
          }}
          data-testid="detail-back"
          className="font-mono text-[11px] tracking-widest uppercase text-zinc-500 hover:text-white cursor-pointer"
        >
          ← Back to Browse
        </button>
        {nav.index >= 0 && nav.total > 1 && (
          <div className="flex items-center gap-2" data-testid="detail-nav">
            <button
              type="button"
              onClick={() => nav.prev && navigate(`/listing/${nav.prev}`)}
              disabled={!nav.prev}
              data-testid="detail-nav-prev"
              title="Previous listing (←)"
              className="h57-btn-secondary !py-1.5 !px-2.5 text-xs inline-flex items-center gap-1 disabled:opacity-30 disabled:cursor-not-allowed"
            >
              <CaretLeft size={14} weight="bold" /> Prev
            </button>
            <span
              className="font-mono text-[10px] text-zinc-500 tracking-widest uppercase tabular-nums"
              data-testid="detail-nav-position"
            >
              {nav.index + 1} / {nav.total}
            </span>
            <button
              type="button"
              onClick={() => nav.next && navigate(`/listing/${nav.next}`)}
              disabled={!nav.next}
              data-testid="detail-nav-next"
              title="Next listing (→)"
              className="h57-btn-secondary !py-1.5 !px-2.5 text-xs inline-flex items-center gap-1 disabled:opacity-30 disabled:cursor-not-allowed"
            >
              Next <CaretRight size={14} weight="bold" />
            </button>
          </div>
        )}
      </div>

      <div className="mt-4 grid md:grid-cols-2 gap-6 md:gap-10">
        <div className="space-y-3">
          <div className="aspect-[3/4] bg-[#0A0A0A] h57-surface overflow-hidden relative" data-needle-anchor="image">
            {front ? <img src={front} alt={listing.title} className="w-full h-full object-contain" decoding="async" fetchPriority="high" /> : <div className="w-full h-full flex items-center justify-center text-zinc-700">No image</div>}
          </div>
          {back && (
            <div className="aspect-[3/4] bg-[#0A0A0A] h57-surface overflow-hidden max-w-[50%]">
              <img src={back} alt="back" className="w-full h-full object-contain" loading="lazy" decoding="async" />
            </div>
          )}
        </div>

        <div>
          <div className="flex flex-wrap gap-1.5 mb-3">
            {chips.map((c, i) => <span key={i} className={`h57-chip ${c.cls}`}>{c.label}</span>)}
            <span className="h57-chip">{listing.sport}</span>
          </div>
          <h1 className="font-display font-black uppercase text-2xl md:text-3xl tracking-tight leading-tight relative" data-needle-anchor="title" data-testid="detail-title">{listing.title}</h1>

          <div className="mt-4 flex items-baseline gap-4 flex-wrap relative" data-needle-anchor="price">
            <div className="font-display font-black text-4xl text-[#FF5F1F]" data-testid="detail-price">${Number(listing.price).toFixed(2)}</div>
            {listing.shipping_price > 0 && (
              <div className="font-mono text-xs text-zinc-500 uppercase">
                + ${Number(listing.shipping_price).toFixed(2)} ship
              </div>
            )}
            {listing.accepts_offers ? (
              <span
                data-testid="detail-offers-yes"
                title="Seller accepts offers"
                className="inline-flex items-center gap-1 border border-emerald-500 text-emerald-400 px-2 py-1 uppercase tracking-widest text-[10px] font-bold"
              >
                <ArrowsClockwise size={11} weight="bold" /> Offers
              </span>
            ) : (
              <span
                data-testid="detail-offers-firm"
                title="Firm price — seller not accepting offers"
                className="inline-flex items-center border border-red-500 text-red-400 px-2 py-1 uppercase tracking-widest text-[10px] font-bold"
              >
                Firm
              </span>
            )}
            <LikeButton
              listingId={listing.id}
              initialLiked={likedByMe}
              initialCount={listing.likes_count || 0}
              size="md"
            />
          </div>

          {listing.status !== "active" && (
            <div className="mt-4 h57-chip !py-2 !px-3 h57-chip-gold">Status: {listing.status}</div>
          )}

          {isOwn && (
            <div className="mt-4 flex flex-wrap gap-2" data-testid="detail-owner-actions">
              <Link
                to={`/listing/${listing.id}/edit`}
                data-testid="detail-edit-listing"
                className="h57-btn-secondary text-xs inline-flex items-center gap-2"
              >
                Edit Listing →
              </Link>
              <div className="font-mono text-[10px] text-zinc-500 uppercase tracking-widest self-center">
                You&apos;re the seller
              </div>
            </div>
          )}

          {/* Seller card */}
          {seller && (
            <Link to={`/u/${seller.username}`} data-testid="detail-seller-link" data-needle-anchor="seller" className="mt-6 h57-surface p-4 block hover:border-white/30 transition-colors relative">
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-mono text-[10px] text-zinc-500 uppercase tracking-widest">Seller</div>
                  <div className="font-display font-bold text-lg inline-flex items-center gap-1.5">
                    @{seller.username}
                    <FoundingHat founding={seller.founding_member} size={14} />
                  </div>
                  {seller.founding_member && <span className="h57-chip h57-chip-gold mt-1">Founding</span>}
                </div>
                <div className="text-right">
                  <div className="font-mono text-[10px] text-zinc-500 uppercase tracking-widest">Sales</div>
                  <div className="font-display font-black text-xl">{seller.confirmed_sales_count || 0}</div>
                </div>
              </div>
              {seller.typical_ship_time && (
                <div className="mt-3 flex items-center gap-2 text-sm text-zinc-400"><Truck size={14} weight="bold" /> Ships in {seller.typical_ship_time}</div>
              )}
              {seller.ships_from_state && (
                <div className="mt-1 flex items-center gap-2 text-sm text-zinc-400"><Package size={14} weight="bold" /> From {seller.ships_from_state}</div>
              )}
              <div className="mt-2 font-mono text-[10px] text-[#FF5F1F] tracking-widest uppercase">View profile →</div>
            </Link>
          )}

          {/* Actions */}
          {!isOwn && listing.status === "active" && (
            <div className="mt-6 space-y-3">
              <button onClick={openPurchase} disabled={txBusy} data-testid="detail-purchase"
                className="w-full inline-flex items-center justify-center gap-2 h57-btn-primary py-4 text-base">
                <HandCoins size={18} weight="fill" /> {txBusy ? "Sending…" : `Buy Now · $${Number(listing.price || 0).toFixed(2)}`}
              </button>
              <button
                onClick={() => { if (!user) { navigate("/login"); return; } cart.add(id); }}
                disabled={txBusy}
                data-testid="detail-add-to-cart"
                className="w-full inline-flex items-center justify-center gap-2 h57-btn-secondary py-3 text-sm"
              >
                <ShoppingCartSimple size={16} weight="bold" /> Add to Cart
              </button>
              <div className="grid grid-cols-2 gap-3">
                <button onClick={() => setMsgOpen(true)} data-testid="detail-message" className="h57-btn-secondary flex items-center justify-center gap-2">
                  <ChatCircle size={16} weight="bold" /> Message
                </button>
                <button onClick={toggleWatch} data-testid="detail-watch" className={`h57-btn-secondary flex items-center justify-center gap-2 ${saved ? "!border-[#FF5F1F] !text-[#FF5F1F]" : ""}`}>
                  <Heart size={16} weight={saved ? "fill" : "bold"} /> {saved ? "Saved" : "Watch"}
                </button>
              </div>
              <p className="text-[11px] text-zinc-500 text-center leading-relaxed">
                Purchase opens a two-party order — seller validates, you pay via their payment link, both parties confirm.
                Hunt57 never touches your money. Read the flow on your Sales page.
              </p>
              <button onClick={() => setReportOpen(true)} data-testid="detail-report" className="text-xs text-zinc-500 hover:text-red-400 flex items-center gap-1 mt-2">
                <Flag size={12} weight="bold" /> Report Listing
              </button>
            </div>
          )}

          {!isOwn && listing.status === "pending" && (
            <div className="mt-6 p-3 border border-[#FFD700]/40 bg-[#FFD700]/5 text-sm text-zinc-300" data-testid="detail-pending-notice">
              <div className="font-mono text-[10px] uppercase tracking-widest text-[#FFD700]">→ Sale in progress</div>
              <p className="mt-1">This card has an active Purchase Order. If the PO expires or is declined, it&apos;ll return to the browse board.</p>
            </div>
          )}

          {isOwn && listing.status === "active" && (
            <div className="mt-6">
              <div className="text-xs text-zinc-500 font-mono uppercase tracking-widest">Your listing — buyers open POs directly on this page.</div>
            </div>
          )}

          {/* Transactions on this listing (any status) — visible to parties */}
          {transactions.length > 0 && (
            <div className="mt-6 h57-surface p-4" data-testid="detail-tx-list">
              <div className="font-mono text-[10px] uppercase tracking-widest text-[#FF5F1F]">→ Sale Records</div>
              <div className="mt-2 space-y-2">
                {transactions.map((t) => (
                  <Link key={t.id} to="/transactions" data-testid={`detail-tx-${t.id}`}
                    className="block p-2 border border-white/10 hover:border-[#FF5F1F]/60 text-xs transition-colors">
                    <div className="flex items-center justify-between">
                      <span>@{t.buyer_username} ↔ @{t.seller_username}</span>
                      <span className="font-mono uppercase tracking-widest text-[#FF5F1F]">{t.status}</span>
                    </div>
                    <div className="text-zinc-500 mt-0.5">
                      Buyer: {t.buyer_confirmed_at ? "✓" : "·"} · Seller: {t.seller_confirmed_at ? "✓" : "·"}
                      {t.feedback_stars ? ` · ${t.feedback_stars}★` : ""}
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}

          {/* Details */}
          <div className="mt-8 h57-surface p-4 relative" data-needle-anchor="details">
            <h3 className="font-display font-bold uppercase text-xs tracking-widest text-zinc-400 mb-3">Card Details</h3>
            <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
              {[
                ["Player", listing.player_name],
                ["Sport", listing.sport],
                ["Year", listing.year],
                ["Brand", listing.brand],
                ["Set", listing.set_name],
                ["Team", listing.team],
                ["Card #", listing.card_number],
                ["Parallel", listing.parallel],
                ["Condition", listing.condition],
                ["Grading", listing.graded ? `${listing.grading_company || ""} ${listing.grade || ""}`.trim() : "Raw"],
                ["Cert #", listing.cert_number],
              ].filter(([, v]) => v).map(([k, v]) => (
                <div key={k} className="flex justify-between border-b border-white/5 py-1">
                  <dt className="text-zinc-500">{k}</dt>
                  <dd className="text-white text-right font-medium">{v}</dd>
                </div>
              ))}
            </dl>
          </div>

          {listing.notes && (
            <div className="mt-4 h57-surface p-4 relative" data-needle-anchor="notes">
              <h3 className="font-display font-bold uppercase text-xs tracking-widest text-zinc-400 mb-2">Notes</h3>
              <p className="text-sm text-zinc-300 whitespace-pre-wrap">{listing.notes}</p>
            </div>
          )}

          {listing.combined_shipping?.enabled && (
            <div className="mt-4 h57-surface p-4 border-[#007AFF]/40 relative" data-needle-anchor="shipping" data-testid="detail-combined-shipping">
              <div className="flex items-center gap-2 mb-3">
                <Package size={16} weight="fill" className="text-[#007AFF]" />
                <h3 className="font-display font-bold uppercase text-xs tracking-widest text-[#007AFF]">Combined Shipping Available</h3>
              </div>
              <div className="grid grid-cols-3 gap-3 text-sm">
                <div>
                  <div className="font-display font-black text-lg tabular-nums text-white">
                    ${Number(listing.combined_shipping.first).toFixed(2)}
                  </div>
                  <div className="font-mono text-[9px] uppercase tracking-widest text-zinc-500 mt-0.5">Initial charge</div>
                </div>
                <div>
                  <div className="font-display font-black text-lg tabular-nums text-white">
                    ${Number(listing.combined_shipping.addl).toFixed(2)}
                  </div>
                  <div className="font-mono text-[9px] uppercase tracking-widest text-zinc-500 mt-0.5">Per additional card</div>
                </div>
                <div>
                  <div className="font-display font-black text-lg tabular-nums text-white">
                    {listing.combined_shipping.max}
                  </div>
                  <div className="font-mono text-[9px] uppercase tracking-widest text-zinc-500 mt-0.5">Max cards / package</div>
                </div>
              </div>
              <p className="mt-3 text-[11px] leading-relaxed text-zinc-500">
                Buy multiple cards from @{seller?.username} in one shipment to save on shipping. Once the max is reached, a new full shipping charge begins for the next package.
              </p>
            </div>
          )}

          <div className="mt-6">
            <PaymentDisclaimer />
          </div>
        </div>
      </div>

      {/* Purchase Order modal — legal acknowledgment required */}
      {purchaseOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={() => !txBusy && setPurchaseOpen(false)}>
          <div className="w-full max-w-lg bg-[#121212] border border-white/10 p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="purchase-modal">
            <h3 className="font-display font-black uppercase text-lg">Open Purchase Order</h3>
            <p className="text-xs text-zinc-500 mt-1">Seller: @{seller?.username}</p>

            <div className="mt-4 p-3 border border-white/10 bg-[#0A0A0A] text-sm space-y-1">
              <div className="text-zinc-200 font-semibold">{listing.title}</div>
              <div className="font-display font-black text-2xl text-[#FF5F1F]">${Number(listing.price || 0).toFixed(2)}</div>
              {listing.shipping_price > 0 && (
                <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">+ ${Number(listing.shipping_price).toFixed(2)} shipping</div>
              )}
            </div>

            <p className="mt-4 text-xs leading-relaxed text-zinc-400">
              The seller has 72 hours to validate. When they accept, you&apos;ll pay them directly via your chosen method. Hunt57 does not process the payment.
            </p>

            {/* Payment method picker — buyer must choose one of the seller's offered paths. */}
            <div className="mt-4">
              <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-2">→ How will you pay?</div>
              <div className="grid grid-cols-2 gap-2" data-testid="purchase-method-picker">
                {[
                  { key: "paypal",      label: "PayPal",       available: !!seller?.paypal_link },
                  { key: "cashapp",     label: "Cash App",     available: !!seller?.cashapp_handle },
                  { key: "venmo",       label: "Venmo",        available: !!seller?.venmo_link },
                  { key: "credit_card", label: "Credit Card",  available: !!seller?.accepts_credit_card },
                  { key: "other",       label: seller?.other_payment_notes ? "Other" : null, available: !!seller?.other_payment_notes },
                ].filter((m) => m.label).map((m) => {
                  const selected = chosenMethod === m.key;
                  return (
                    <button
                      key={m.key}
                      type="button"
                      onClick={() => m.available && setChosenMethod(m.key)}
                      disabled={!m.available}
                      data-testid={`purchase-method-${m.key}`}
                      className={`text-left p-3 border transition-colors ${
                        selected
                          ? "border-[#FF5F1F] bg-[#FF5F1F]/10 text-white"
                          : m.available
                            ? "border-white/15 hover:border-white/30 text-zinc-300"
                            : "border-white/5 text-zinc-600 cursor-not-allowed"
                      }`}
                    >
                      <div className="font-display font-bold text-sm">{m.label}</div>
                      <div className="font-mono text-[9px] uppercase tracking-widest mt-0.5 text-zinc-500">
                        {m.available ? (selected ? "Selected" : "Available") : "Not offered"}
                      </div>
                    </button>
                  );
                })}
              </div>
              {chosenMethod === "credit_card" && (
                <div className="mt-2 p-2 border border-[#FFD700]/30 bg-[#FFD700]/5 text-[11px] text-zinc-300">
                  When the seller validates, they&apos;ll paste a unique credit-card pay link (Stripe / Square / PayPal invoice) for this order.
                  {seller?.credit_card_note && (
                    <div className="mt-1 text-zinc-400 italic">Seller note: {seller.credit_card_note}</div>
                  )}
                </div>
              )}
            </div>

            <div className="mt-4">
              <LegalNotice
                variant="transaction"
                checked={acceptPurchaseTerms}
                onChange={setAcceptPurchaseTerms}
                testId="purchase-legal"
              />
            </div>

            <div className="mt-4 flex gap-3">
              <button type="button" onClick={() => setPurchaseOpen(false)} disabled={txBusy}
                className="h57-btn-secondary flex-1" data-testid="purchase-cancel">Cancel</button>
              <button type="button" onClick={submitPurchase}
                disabled={txBusy || !acceptPurchaseTerms || !chosenMethod}
                className="h57-btn-primary flex-1" data-testid="purchase-submit">
                {txBusy ? "Opening…" : "Open Purchase Order"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Message modal */}
      {msgOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={() => setMsgOpen(false)}>
          <div className="w-full max-w-md bg-[#121212] border border-white/10 p-6" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-display font-black uppercase text-lg">Message @{seller?.username}</h3>
            <p className="text-xs text-zinc-500 mt-1">Re: {listing.title}</p>
            {listing.accepts_offers === false && (
              <div
                data-testid="msg-modal-firm-warning"
                className="mt-4 flex items-start gap-2 border border-[#FFD700]/50 bg-[#FFD700]/5 px-3 py-2 text-[11px] leading-relaxed text-[#FFD700]"
              >
                <Warning size={16} weight="fill" className="shrink-0 mt-0.5" />
                <div>
                  <span className="font-mono uppercase tracking-widest text-[10px] font-bold">FIRM listing</span>
                  <span className="text-zinc-300"> — seller is not accepting offers. Sending an offer anyway may result in an account strike and possible suspension.</span>
                </div>
              </div>
            )}
            <textarea data-testid="msg-modal-text" className="h57-input mt-4 min-h-[120px]" value={msgText} onChange={(e) => setMsgText(e.target.value)} placeholder="Hey, is this still available?" />
            <div className="mt-4 flex gap-3">
              <button onClick={() => setMsgOpen(false)} className="h57-btn-secondary flex-1" data-testid="msg-modal-cancel">Cancel</button>
              <button onClick={sendMessage} className="h57-btn-primary flex-1" data-testid="msg-modal-send">Send</button>
            </div>
          </div>
        </div>
      )}

      {/* Payment external redirect modal */}
      {paymentModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={() => setPaymentModal(null)}>
          <div className="w-full max-w-md bg-[#121212] border border-[#FFD700]/30 p-6" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-display font-black uppercase text-lg text-[#FFD700]">Leaving Hunt57</h3>
            <p className="text-sm text-zinc-300 mt-3">
              You are leaving Hunt57 to pay the seller directly via <span className="uppercase font-bold text-white">{paymentModal.method}</span>. Hunt57 does not process this card-sale payment.
            </p>
            {paymentModal.method === "cashapp" && paymentModal.link ? (
              <div className="mt-4 p-3 bg-[#0A0A0A] border border-white/10">
                <div className="font-mono text-xs text-zinc-500 uppercase tracking-widest">Cash App</div>
                <div className="font-display font-bold text-xl mt-1" data-testid="cashapp-handle">{paymentModal.link}</div>
                <button onClick={() => { navigator.clipboard.writeText(paymentModal.link); toast.success("Copied"); }} className="h57-btn-secondary text-xs mt-3 w-full" data-testid="cashapp-copy">Copy Handle</button>
              </div>
            ) : null}
            <div className="mt-6 flex gap-3">
              <button onClick={() => setPaymentModal(null)} className="h57-btn-secondary flex-1" data-testid="pay-modal-cancel">Cancel</button>
              {paymentModal.link && paymentModal.method !== "cashapp" && (
                <a href={paymentModal.link.startsWith("http") ? paymentModal.link : `https://${paymentModal.link}`} target="_blank" rel="noopener noreferrer" data-testid="pay-modal-continue" className="h57-btn-primary flex-1 text-center">
                  Continue →
                </a>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Report modal */}
      {reportOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={() => setReportOpen(false)}>
          <div className="w-full max-w-md bg-[#121212] border border-white/10 p-6" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-display font-black uppercase text-lg">Report Listing</h3>
            <label className="h57-label mt-4">Reason</label>
            <select className="h57-input" data-testid="report-reason" value={reportReason} onChange={(e) => setReportReason(e.target.value)}>
              {["Suspected scam", "Fake card/counterfeit concern", "Misleading listing", "Item not as described", "Payment issue", "Harassment", "Spam", "Other"].map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
            <div className="mt-6 flex gap-3">
              <button onClick={() => setReportOpen(false)} className="h57-btn-secondary flex-1" data-testid="report-cancel">Cancel</button>
              <button onClick={submitReport} className="h57-btn-primary flex-1" data-testid="report-submit">Submit</button>
            </div>
          </div>
        </div>
      )}

      {/* Buy modal removed — Purchase button opens a PO directly (with a confirm). */}
    </div>
  );
}
